// Dialer libre global (port del GlobalDialer del CRM,
// crm/apps/web/src/features/phone/components/GlobalDialer.tsx) a las
// convenciones de este SPA: inline styles, español, sin Tailwind ni i18n.
//
// Botón "marcador" (icono teléfono) en el header del Layout, junto al
// PhoneProviderSelector y con el MISMO gating (solo con sesión CRM conectada +
// preferencia cargada). Abre un popover anclado con:
//   - selector de país (hint de normalización: nacional pelado → +CC; el server
//     normaliza a E.164, default ES si no se indica),
//   - input de número (cualquier formato: +34…, 0034…, nacional),
//   - botón Llamar y errores claros (describeCrmError / roles).
//
// La llamada entra en el MISMO ciclo de PhoneContext que las llamadas a lead
// (startDialCall → dialNumber): la consola flotante existente se abre sola con
// la llamada (activeCall pasa a no-null). Si el número pertenece a un lead, la
// consola lo muestra (nombre + ficha) y las notas van a su timeline; si no,
// consola sin ficha y notas ocultas. Esta UI NO duplica el estado de la llamada.

import { memo, useEffect, useRef, useState } from "react";
import { CrmHttpError, describeCrmError } from "../../api/crm";
import { DialMarket } from "../../api/crmTypes";
import { useCrmSession } from "../../context/CrmSessionContext";
import { usePhone } from "../../context/PhoneContext";

const MARKETS: DialMarket[] = ["ES", "IT", "FR", "PT"];

function GlobalDialerInner() {
  const { connected } = useCrmSession();
  const { prefsReady, provider, softphone, startDialCall, activeCall } = usePhone();

  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState("");
  const [market, setMarket] = useState<DialMarket>("ES");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    function onClickOutside(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onEscape(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEscape);
    };
  }, [open]);

  // Mismo gating que el selector de proveedor: si el usuario no usa el CRM el
  // header queda intacto.
  if (connected !== true || !prefsReady) return null;

  const busy =
    pending || (activeCall !== null && activeCall.live !== "completed");

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = number.trim();
    if (!trimmed || busy) return;
    setError(null);
    setPending(true);
    try {
      await startDialCall({ number: trimmed, market });
      // La consola flotante se abre sola con la llamada (activeCall). Cerramos el
      // popover y limpiamos el número.
      setOpen(false);
      setNumber("");
    } catch (err) {
      setError(
        err instanceof CrmHttpError
          ? describeCrmError(err)
          : err instanceof Error
            ? err.message
            : "Error inesperado",
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          setError(null);
        }}
        title="Marcar un número"
        aria-label="Marcar un número"
        aria-expanded={open}
        style={{
          // Un solo teléfono en el header (petición Yago): el 📞 rojo,
          // encuadrado como el resto de botones. Abre el marcador al click.
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "2.1rem",
          height: "2.1rem",
          borderRadius: "0.45rem",
          border: open ? "1px solid #0069d9" : "1px solid #dee2e6",
          background: open ? "#e7f1ff" : "white",
          fontSize: "1rem",
          lineHeight: 1,
          cursor: "pointer",
        }}
      >
        <span aria-hidden>📞</span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Marcador"
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 0.4rem)",
            zIndex: 150,
            width: "18rem",
            background: "white",
            border: "1px solid #dee2e6",
            borderRadius: "0.6rem",
            boxShadow: "0 12px 34px rgba(0,0,0,.18)",
            padding: "0.7rem",
            color: "#1f2933",
          }}
        >
          <form
            onSubmit={onSubmit}
            style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
          >
            <div style={{ fontSize: "0.82rem", fontWeight: 700 }}>
              Marcar un número
            </div>
            <div style={{ display: "flex", gap: "0.4rem" }}>
              <input
                ref={inputRef}
                type="tel"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="+34 612 345 678"
                style={{
                  flex: 1,
                  minWidth: 0,
                  boxSizing: "border-box",
                  borderRadius: "0.4rem",
                  border: "1px solid #dee2e6",
                  padding: "0.4rem 0.5rem",
                  fontSize: "0.85rem",
                  fontFamily: "inherit",
                  color: "#1f2933",
                }}
              />
              <select
                value={market}
                onChange={(e) => setMarket(e.target.value as DialMarket)}
                title="País para números sin prefijo internacional"
                style={{
                  borderRadius: "0.4rem",
                  border: "1px solid #dee2e6",
                  padding: "0.4rem 0.35rem",
                  fontSize: "0.85rem",
                  background: "white",
                  color: "#1f2933",
                  cursor: "pointer",
                }}
              >
                {MARKETS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              disabled={!number.trim() || busy}
              style={{
                borderRadius: "0.4rem",
                border: "1px solid #0069d9",
                background: "#0069d9",
                color: "white",
                padding: "0.45rem 0.7rem",
                fontSize: "0.85rem",
                fontWeight: 700,
                cursor: !number.trim() || busy ? "not-allowed" : "pointer",
                opacity: !number.trim() || busy ? 0.55 : 1,
              }}
            >
              {pending ? "Llamando…" : "📞 Llamar"}
            </button>
            <p style={{ fontSize: "0.7rem", color: "#73879C", margin: 0 }}>
              {provider === "telnyx"
                ? "El audio suena en este navegador (softphone Telnyx)."
                : "El audio suena en la app de agente de Cloudtalk."}
            </p>
            {activeCall !== null && activeCall.live !== "completed" && (
              <div style={{ fontSize: "0.72rem", color: "#856404" }}>
                Ya hay una llamada en curso — ciérrala antes de marcar otra.
              </div>
            )}
            {provider === "telnyx" && softphone.status !== "idle" && (
              <div style={{ fontSize: "0.72rem", color: "#856404" }}>
                El softphone Telnyx aún no está listo (o hay otra llamada).
              </div>
            )}
            {error && (
              <div
                style={{
                  borderRadius: "0.4rem",
                  border: "1px solid #f5c2c7",
                  background: "#fef2f2",
                  color: "#991b1b",
                  padding: "0.4rem 0.5rem",
                  fontSize: "0.75rem",
                }}
              >
                {error}
              </div>
            )}
          </form>
        </div>
      )}
    </div>
  );
}

// memo: vive en el header de todas las páginas; solo re-renderiza con su estado
// o el de la sesión/telefonía.
const GlobalDialer = memo(GlobalDialerInner);
export default GlobalDialer;
